import React, { useState, useContext, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import { useToast } from '../context/ToastContext';
import { setAdminRole, removeAdminRole, getAllUsers } from '../services/adminApi';
import { Users, Shield, Trash2, Plus } from 'lucide-react';

const AdminManagement = () => {
  const { user } = useAppContext();
  const { addToast } = useToast();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  // ✅ FIXED: Move useEffect BEFORE the admin check
  // This hook runs first, preserving hook order
  useEffect(() => {
    // Only fetch users if the current user is an admin
    if (user && user.admin) {
      fetchUsers();
    }
  }, [user]); // Add user as dependency

  // Now you can safely do the admin check AFTER all hooks
  const fetchUsers = async () => {
    if (!user) return;
    setIsLoadingUsers(true);
    try {
      const usersList = await getAllUsers(user);
      setUsers(usersList);
    } catch (error) {
      addToast(`❌ ${error.message}`, 'error');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleSetAdmin = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      addToast('⚠️ Please enter an email address', 'error');
      return;
    }

    setIsLoading(true);
    try {
      await setAdminRole(email.trim(), user);
      addToast(`✅ ${email} is now an admin!`, 'success');
      setEmail('');
      fetchUsers();
    } catch (error) {
      addToast(`❌ ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveAdmin = async (userEmail) => {
    if (!window.confirm(`Are you sure you want to remove admin privileges from ${userEmail}?`)) {
      return;
    }

    setIsLoading(true);
    try {
      await removeAdminRole(userEmail, user);
      addToast(`✅ Admin role removed from ${userEmail}`, 'success');
      fetchUsers();
    } catch (error) {
      addToast(`❌ ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ FIXED: Admin check happens AFTER all hooks are called
  if (!user || !user.admin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4">
        <div className="bg-white p-8 rounded-lg shadow-2xl text-center max-w-md">
          <h1 className="text-3xl font-bold text-red-600 mb-4">❌ Access Denied</h1>
          <p className="text-gray-600 mb-6">You do not have permission to access this page.</p>
          <a href="/" className="text-blue-600 hover:text-blue-700 font-semibold">← Back to Home</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2 flex items-center justify-center gap-3">
            <Shield className="h-10 w-10" /> Admin Management
          </h1>
          <p className="text-slate-300 text-lg">Manage user roles and permissions</p>
        </div>

        {/* Promote User Section */}
        <div className="bg-white rounded-lg shadow-2xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Plus className="h-6 w-6 text-green-600" /> Promote User to Admin
          </h2>
          <p className="text-slate-600 mb-6">Enter a user's email to grant them admin privileges.</p>

          <form className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                User Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition"
              />
            </div>

            <button
              onClick={handleSetAdmin}
              disabled={isLoading}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold py-3 px-4 rounded-lg transition duration-300 flex items-center justify-center gap-2"
            >
              <Plus className="h-5 w-5" />
              {isLoading ? 'Processing...' : 'Make Admin'}
            </button>
          </form>
        </div>

        {/* Users List Section */}
        <div className="bg-white rounded-lg shadow-2xl p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Users className="h-6 w-6 text-blue-600" /> All Users
          </h2>
          <p className="text-slate-600 mb-6">Current admin status of all registered users.</p>

          {isLoadingUsers ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-slate-600">Loading users...</span>
            </div>
          ) : users.length === 0 ? (
            <p className="text-slate-600 text-center py-8">No users found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b-2 border-slate-200">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Email</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Name</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Admin Status</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.uid} className="border-b border-slate-200 hover:bg-slate-50 transition">
                      <td className="px-4 py-3 text-sm text-slate-900">{u.email}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{u.displayName || 'N/A'}</td>
                      <td className="px-4 py-3 text-center">
                        {u.admin ? (
                          <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-semibold">
                            <Shield className="h-4 w-4" /> Admin
                          </span>
                        ) : (
                          <span className="inline-flex items-center bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-xs font-semibold">
                            User
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {u.admin ? (
                          <button
                            onClick={() => handleRemoveAdmin(u.email)}
                            disabled={isLoading}
                            className="text-red-600 hover:text-red-700 disabled:text-gray-400 disabled:cursor-not-allowed transition flex items-center justify-center gap-1 mx-auto"
                            title="Remove Admin"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              setEmail(u.email);
                            }}
                            className="text-blue-600 hover:text-blue-700 transition flex items-center justify-center gap-1 mx-auto"
                            title="Promote to Admin"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 mt-8">
          <h3 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
            <Shield className="h-5 w-5" /> How It Works
          </h3>
          <ul className="text-blue-800 text-sm space-y-2">
            <li>✅ Enter the email of the user you want to promote or demote</li>
            <li>✅ Click "Make Admin" to grant admin privileges</li>
            <li>✅ Click the trash icon to revoke admin privileges</li>
            <li>✅ Users must log out and log back in to see role changes</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminManagement;